<script setup>
import { useAuthStore } from "@/stores/useAuthStore";
import { useContextStore } from "@/stores/useContextStore";
import { useHelpChatStore } from "@/stores/useHelpChatStore";
import { useLoadingStore } from "@/stores/useLoadingStore";
import { useMessageStore } from "@/stores/useMessageStore";
import { useI18n } from "vue-i18n";
import { ContactSupportAPI } from "@/utils/generated/api-common";

const { mdAndUp, xs } = useDisplay();
const { t: $t } = useI18n();
const authStore = useAuthStore();
const contextStore = useContextStore();
const { opened, message } = storeToRefs(useHelpChatStore());
const { setLoading } = useLoadingStore();
const messageStore = useMessageStore();

const [defaultFirstName = "", ...defaultLastName] = (authStore.me?.userName || "").split(" ");

const valid = ref(false);
const firstName = ref(defaultFirstName);
const lastName = ref(defaultLastName.join(" "));
const jobTitle = ref("");
const organization = ref(contextStore.charity?.name || "");
const email = ref(authStore.me?.login || "");
const phoneNumber = ref("");

watch(
  () => contextStore.charity?.name,
  (name) => {
    if (name && !organization.value) organization.value = name;
  }
);

async function submit() {
  try {
    setLoading(true);
    await ContactSupportAPI.createContactSupport({
      firstName: firstName.value,
      lastName: lastName.value,
      jobTitle: jobTitle.value,
      organization: organization.value,
      email: email.value,
      phoneNumber: phoneNumber.value,
      message: message.value,
      pageUrl: document.URL,
    });
    messageStore.addSuccess($t("helpchat.successMessage"));
    message.value = "";
    opened.value = false;
  } finally {
    setLoading(false);
  }
}
</script>

<template>
  <v-dialog v-model="opened" :scrim="false" transition="dialog-bottom-transition" width="500px" class="align-end justify-end" :fullscreen="xs">
    <v-card>
      <v-btn icon="mdi-close" position="absolute" location="top right" variant="text" color="grey" @click.stop="opened = false"></v-btn>
      <v-card-title class="pt-2 pl-sm-4 pr-9 pb-1">
        <v-icon class="mr-2 mt-n2 d-none d-sm-inline-block">mdi-chat-question</v-icon>
        {{ $t("helpchat.leaveMessage") }}
      </v-card-title>
      <v-card-subtitle class="text-caption text-pre-line pb-0">{{ $t("helpchat.textCaption") }}</v-card-subtitle>
      <v-form v-model="valid" @submit.prevent="submit">
        <v-card-text class="pb-2">
          <v-row dense>
            <v-col cols="12" md="6" class="py-2">
              <v-text-field
                v-model="lastName"
                density="compact"
                variant="outlined"
                hide-details
                :label="$t('helpchat.name')"
                :rules="[$v.required(), $v.maxLength(45)]"
                :autofocus="!lastName"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="py-2">
              <v-text-field
                v-model="firstName"
                density="compact"
                variant="outlined"
                hide-details
                :label="$t('helpchat.first_name')"
                :rules="[$v.required(), $v.maxLength(45)]"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="py-2">
              <v-text-field
                v-model="organization"
                density="compact"
                variant="outlined"
                hide-details
                :label="$t('helpchat.organization')"
                :rules="[$v.required(), $v.maxLength(100)]"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="py-2">
              <v-text-field
                v-model="jobTitle"
                density="compact"
                variant="outlined"
                hide-details
                :label="$t('helpchat.job_title')"
                :rules="[$v.required(), $v.maxLength(100)]"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="py-2">
              <v-text-field
                v-model="phoneNumber"
                density="compact"
                variant="outlined"
                hide-details
                :label="$t('helpchat.phonenumber')"
                :rules="[$v.required(), $v.maxLength(25)]"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="py-2">
              <v-text-field
                v-model="email"
                density="compact"
                variant="outlined"
                hide-details
                :label="$t('helpchat.email')"
                :rules="[$v.required(), $v.isEmail(), $v.maxLength(128)]"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col class="py-2">
              <v-textarea
                v-model="message"
                density="compact"
                variant="outlined"
                hide-details
                rows="8"
                :label="$t('helpchat.message')"
                :rules="[$v.required(), $v.maxLength(2000)]"
                :autofocus="!!lastName"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn :disabled="!valid" type="submit" color="primary" variant="flat">{{ $t("helpchat.send") }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-show="!opened"
        v-bind="activatorProps"
        color="grey-darken-3"
        :size="mdAndUp ? 'x-large' : undefined"
        elevation="16"
        position="fixed"
        location="bottom right"
        class="help-chat__button mr-2 mb-2"
        icon="mdi-chat-question"
        rounded="pill"
        :title="$t('helpchat.leaveMessage')"
      ></v-btn>
    </template>
  </v-dialog>
</template>

<style scoped>
.help-chat__button {
  z-index: 1500;
}
</style>
